import { Fragment, useState } from "react";
import { Link } from "@tanstack/react-router";
import { ChevronDownIcon, ChevronRightIcon, FileTextIcon } from "lucide-react";
import { toast } from "sonner";

import { ConfirmDialog } from "~/components/confirm-dialog";
import { DetailField, FieldGrid } from "~/components/detail-field";
import { Pill, StatusBadge } from "~/components/status-badge";
import { TypedConfirmDialog } from "~/components/typed-confirm-dialog";
import { Button } from "~/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "~/components/ui/card";
import { Input } from "~/components/ui/input";
import { Label } from "~/components/ui/label";
import { Skeleton } from "~/components/ui/skeleton";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "~/components/ui/table";
import { ErrorCode, errorMessage, hasCode } from "~/lib/api";
import { EM_DASH, formatDateTime, formatIDR, orDash } from "~/lib/format";
import {
  useCompleteDisbursement,
  useCreateInvoice,
  useDisbursements,
  useFailDisbursement,
  useFetchInvoiceDocument,
  useInvoice,
  useInvoices,
  useIssueInvoice,
  useMerchantBalance,
  usePayInvoice,
  useVoidInvoice,
  type DisbursementListItem,
  type InvoiceListItem,
  type MerchantBalance,
  type MerchantDetail,
} from "~/lib/queries";
import { defaultPeriod, describePeriod, latestPeriodEnd, periodBounds } from "./-statement-period";

/**
 * A merchant's settlement statements and the payouts that pay them
 * (PRO-1400, over the PRO-997/998 API). One cycle, top to bottom:
 *
 *   create a draft for a period -> check it -> issue it (number + PPN)
 *   -> open the document and send it -> pay (reserves net payable)
 *   -> make the bank transfer by hand -> mark transferred (statement paid)
 *
 * The API calls a statement an invoice, and it is one -- Kugie's, for its fee
 * and the PPN on it -- but what the merchant reads it for is how much Kugie is
 * transferring, so the screen calls it a statement.
 *
 * Nothing here moves money. A payout is the record of a transfer someone makes
 * from the bank; the buttons only keep the ledger in step with it.
 */
export function StatementsTab({ merchant }: { merchant: MerchantDetail }) {
  const merchantId = merchant.merchant_id;
  const invoices = useInvoices(merchantId);
  const payouts = useDisbursements({ merchant_id: merchantId, limit: 100 });
  const balance = useMerchantBalance(merchantId);

  if (invoices.isPending) {
    return (
      <div className="space-y-4">
        <Skeleton className="h-32 w-full" />
        <Skeleton className="h-64 w-full" />
      </div>
    );
  }

  if (invoices.error || !invoices.data) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="text-base">Couldn't load statements</CardTitle>
          <CardDescription>{errorMessage(invoices.error)}</CardDescription>
        </CardHeader>
        <CardContent>
          <Button variant="outline" size="sm" onClick={() => void invoices.refetch()}>
            Try again
          </Button>
        </CardContent>
      </Card>
    );
  }

  const statements = invoices.data.items;
  const payoutsByInvoice = groupPayouts(payouts.data?.items ?? []);

  return (
    <div className="space-y-4">
      {!merchant.has_bank_account ? (
        <p className="rounded-md border border-warning/30 bg-warning/10 p-3 text-sm">
          <strong className="font-medium">No bank details on file.</strong> Statements can be created
          and issued, but none can be paid until {merchant.name}'s bank account is set on the merchant
          record.
        </p>
      ) : null}

      <BalanceCard balance={balance.data} loading={balance.isPending} error={balance.error} />

      <NewStatementCard key={defaultPeriod(statements).from} merchantId={merchantId} statements={statements} />

      <Card className="overflow-hidden">
        <CardHeader>
          <CardTitle className="text-base">Statements</CardTitle>
          <CardDescription>
            Newest first. Open a row for its lines and the next step. A void statement keeps its number;
            its settlements go back to be put on a new one.
          </CardDescription>
        </CardHeader>
        <CardContent className="px-0 pb-0">
          {statements.length === 0 ? (
            <p className="px-6 pb-6 text-sm text-muted-foreground">No statements yet.</p>
          ) : (
            <Table>
              <TableHeader>
                <TableRow className="hover:bg-transparent">
                  <TableHead className="w-8 pl-6" />
                  <TableHead>Statement</TableHead>
                  <TableHead>Period</TableHead>
                  <TableHead className="text-right">Kugie fee + PPN</TableHead>
                  <TableHead className="text-right">Net payable</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead className="pr-6">Payout</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {statements.map((statement) => (
                  <StatementRow
                    key={statement.invoice_id}
                    merchant={merchant}
                    statement={statement}
                    payouts={payoutsByInvoice.get(statement.invoice_id) ?? []}
                    balance={balance.data}
                  />
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

/**
 * A statement's payouts, newest first. At most one is live (pending or
 * completed) -- a partial unique index says so -- and any number may have
 * failed before it.
 */
function groupPayouts(payouts: DisbursementListItem[]): Map<string, DisbursementListItem[]> {
  const byInvoice = new Map<string, DisbursementListItem[]>();
  for (const payout of payouts) {
    if (!payout.invoice_id) continue;
    const list = byInvoice.get(payout.invoice_id) ?? [];
    list.push(payout);
    byInvoice.set(payout.invoice_id, list);
  }
  for (const list of byInvoice.values()) {
    list.sort((a, b) => b.created_at.localeCompare(a.created_at));
  }
  return byInvoice;
}

function livePayout(payouts: DisbursementListItem[]): DisbursementListItem | undefined {
  return payouts.find((p) => p.status === "pending" || p.status === "completed");
}

function bankLine(merchant: MerchantDetail): string {
  return `${orDash(merchant.bank_code)} ••••${merchant.bank_account_last4 ?? ""} · ${orDash(merchant.bank_account_holder)}`;
}

function BalanceCard({
  balance,
  loading,
  error,
}: {
  balance?: MerchantBalance;
  loading: boolean;
  error: unknown;
}) {
  return (
    <Card>
      <CardContent className="flex flex-wrap items-center gap-x-6 gap-y-2 pt-6 text-sm">
        <div>
          <span className="block text-xs font-medium text-muted-foreground">Balance owed to the merchant</span>
          {loading ? (
            <Skeleton className="mt-1 h-5 w-32" />
          ) : error || !balance ? (
            <span className="text-destructive">{errorMessage(error)}</span>
          ) : (
            <span className="text-lg font-medium tabular-nums">{formatIDR(balance.payable)}</span>
          )}
        </div>
        {balance && !balance.verified ? (
          <Pill tone="danger" title="The projected balance disagrees with the ledger entries. Don't pay until it's rebuilt.">
            balance not verified
          </Pill>
        ) : null}
        <p className="max-w-xl text-xs text-muted-foreground">
          Everything settled and not yet paid out, net of Kugie's fee and of issued statements' PPN. A
          pending payout is already off it.
        </p>
      </CardContent>
    </Card>
  );
}

function NewStatementCard({ merchantId, statements }: { merchantId: string; statements: InvoiceListItem[] }) {
  const initial = defaultPeriod(statements);
  const [from, setFrom] = useState(initial.from);
  const [to, setTo] = useState(initial.to);
  const [formError, setFormError] = useState<string | null>(null);
  const create = useCreateInvoice(merchantId);
  const maxEnd = latestPeriodEnd();

  function submit() {
    setFormError(null);
    if (!from || !to) {
      setFormError("Pick both days.");
      return;
    }
    if (from > to) {
      setFormError("The period starts after it ends.");
      return;
    }
    if (to > maxEnd) {
      setFormError("A period has to be over before it's put on a statement. End it yesterday at the latest.");
      return;
    }
    create.mutate(periodBounds(from, to), {
      onSuccess: (created) => {
        toast.success(`Draft statement created for ${describePeriod(created)}. Check it, then issue it.`);
      },
    });
  }

  const error =
    formError ??
    (create.error
      ? hasCode(create.error, ErrorCode.NothingToInvoice)
        ? "Nothing to put on a statement: no settlements in this period that aren't on one already."
        : errorMessage(create.error)
      : null);

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base">New statement</CardTitle>
        <CardDescription>
          Covers every settlement Durianpay posted between these days (Jakarta time, both days included)
          that isn't on a statement already. A payment that hasn't settled by the end of the period goes
          on the next one. The dates start where the last statement ended.
        </CardDescription>
      </CardHeader>
      <CardContent>
        <form
          onSubmit={(event) => {
            event.preventDefault();
            submit();
          }}
          className="flex flex-wrap items-end gap-3"
        >
          <div className="space-y-1.5">
            <Label htmlFor="statement-from" className="text-xs text-muted-foreground">
              From
            </Label>
            <Input
              id="statement-from"
              type="date"
              max={maxEnd}
              value={from}
              onChange={(e) => setFrom(e.target.value)}
              className="w-40"
            />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="statement-to" className="text-xs text-muted-foreground">
              Through
            </Label>
            <Input
              id="statement-to"
              type="date"
              max={maxEnd}
              value={to}
              onChange={(e) => setTo(e.target.value)}
              className="w-40"
            />
          </div>
          <Button type="submit" size="sm" disabled={create.isPending}>
            {create.isPending ? "Creating…" : "Create draft"}
          </Button>
          {error ? <span className="text-xs text-destructive">{error}</span> : null}
        </form>
      </CardContent>
    </Card>
  );
}

function StatementRow({
  merchant,
  statement,
  payouts,
  balance,
}: {
  merchant: MerchantDetail;
  statement: InvoiceListItem;
  payouts: DisbursementListItem[];
  balance?: MerchantBalance;
}) {
  const [open, setOpen] = useState(false);
  const live = livePayout(payouts);
  const Chevron = open ? ChevronDownIcon : ChevronRightIcon;

  return (
    <Fragment>
      <TableRow className="cursor-pointer" onClick={() => setOpen((v) => !v)} aria-expanded={open}>
        <TableCell className="pl-6">
          <Chevron className="size-4 text-muted-foreground" />
        </TableCell>
        <TableCell className="font-medium">
          {statement.invoice_number ?? <span className="text-muted-foreground">Draft</span>}
        </TableCell>
        <TableCell className="text-muted-foreground">{describePeriod(statement)}</TableCell>
        <TableCell className="text-right tabular-nums text-muted-foreground">
          {formatIDR(statement.totals.total_due)}
        </TableCell>
        <TableCell className="text-right font-medium tabular-nums">{formatIDR(statement.totals.net_payable)}</TableCell>
        <TableCell>
          <StatusBadge status={statement.status} />
        </TableCell>
        <TableCell className="pr-6" onClick={(e) => e.stopPropagation()}>
          {live ? (
            <Link
              to="/disbursements/$disbursementId"
              params={{ disbursementId: live.disbursement_id }}
              className="text-primary hover:underline"
            >
              <StatusBadge status={live.status === "pending" ? "pending" : "completed"} />
            </Link>
          ) : (
            EM_DASH
          )}
        </TableCell>
      </TableRow>
      {open ? (
        <TableRow className="hover:bg-transparent">
          <TableCell colSpan={7} className="bg-muted/30 px-6 py-4 whitespace-normal">
            <StatementDetail merchant={merchant} statement={statement} payouts={payouts} balance={balance} />
          </TableCell>
        </TableRow>
      ) : null}
    </Fragment>
  );
}

function StatementDetail({
  merchant,
  statement,
  payouts,
  balance,
}: {
  merchant: MerchantDetail;
  statement: InvoiceListItem;
  payouts: DisbursementListItem[];
  balance?: MerchantBalance;
}) {
  const detail = useInvoice(merchant.merchant_id, statement.invoice_id);
  const live = livePayout(payouts);
  const lastFailed = live ? undefined : payouts.find((p) => p.status === "failed");
  const totals = statement.totals;

  return (
    <div className="space-y-4">
      <FieldGrid className="lg:grid-cols-4">
        <DetailField label="Buyers paid (gross)">{formatIDR(totals.gross)}</DetailField>
        <DetailField label="Kugie fee">{formatIDR(totals.platform_fee)}</DetailField>
        <DetailField label="PPN on the fee">
          {formatIDR(totals.ppn)} <span className="text-muted-foreground">(DPP {formatIDR(totals.dpp)})</span>
        </DetailField>
        <DetailField label="Net payable to the merchant">
          <strong className="font-medium">{formatIDR(totals.net_payable)}</strong>
        </DetailField>
        <DetailField label="Created">
          {formatDateTime(statement.created_at)} · {orDash(statement.created_by_email)}
        </DetailField>
        {statement.issued_at ? <DetailField label="Issued">{formatDateTime(statement.issued_at)}</DetailField> : null}
        {statement.paid_at ? <DetailField label="Paid">{formatDateTime(statement.paid_at)}</DetailField> : null}
        {statement.voided_at ? (
          <DetailField label="Voided">
            {formatDateTime(statement.voided_at)} · {orDash(statement.voided_by_email)}
            {statement.void_reason ? ` · "${statement.void_reason}"` : ""}
          </DetailField>
        ) : null}
      </FieldGrid>

      <div className="overflow-hidden rounded-md border bg-card">
        <Table>
          <TableHeader>
            <TableRow className="hover:bg-transparent">
              <TableHead className="pl-4">Line</TableHead>
              <TableHead className="text-right">Payments</TableHead>
              <TableHead className="text-right">Gross</TableHead>
              <TableHead className="text-right">Kugie fee</TableHead>
              <TableHead className="pr-4 text-right">PPN</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {detail.isPending ? (
              <TableRow>
                <TableCell colSpan={5} className="pl-4">
                  <Skeleton className="h-4 w-64" />
                </TableCell>
              </TableRow>
            ) : detail.error || !detail.data ? (
              <TableRow>
                <TableCell colSpan={5} className="pl-4 text-destructive">
                  {errorMessage(detail.error)}
                </TableCell>
              </TableRow>
            ) : (
              detail.data.lines.map((line) => (
                <TableRow key={line.line_no}>
                  <TableCell className="pl-4 whitespace-normal">{line.description}</TableCell>
                  <TableCell className="text-right tabular-nums">{line.journal_count}</TableCell>
                  <TableCell className="text-right tabular-nums">{formatIDR(line.gross)}</TableCell>
                  <TableCell className="text-right tabular-nums">{formatIDR(line.platform_fee)}</TableCell>
                  <TableCell className="pr-4 text-right tabular-nums">{formatIDR(line.ppn)}</TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>

      {live ? <PayoutSummary payout={live} /> : null}
      {lastFailed ? (
        <p className="text-xs text-muted-foreground">
          An earlier payout failed {formatDateTime(lastFailed.failed_at)}
          {lastFailed.failure_reason ? `: "${lastFailed.failure_reason}"` : ""}. The money went back on the
          balance.
        </p>
      ) : null}

      <div className="flex flex-wrap items-center gap-2">
        <OpenDocumentButton merchantId={merchant.merchant_id} statement={statement} />
        {statement.status === "draft" ? (
          <>
            <IssueAction merchantId={merchant.merchant_id} statement={statement} />
            <DiscardAction merchantId={merchant.merchant_id} statement={statement} />
          </>
        ) : null}
        {statement.status === "issued" && !live ? (
          <>
            <PayAction merchant={merchant} statement={statement} balance={balance} />
            <VoidAction merchantId={merchant.merchant_id} statement={statement} />
          </>
        ) : null}
        {statement.status === "issued" && live?.status === "pending" ? (
          <>
            <CompleteAction merchantId={merchant.merchant_id} statement={statement} payout={live} />
            <FailAction merchantId={merchant.merchant_id} statement={statement} payout={live} />
          </>
        ) : null}
      </div>
    </div>
  );
}

function PayoutSummary({ payout }: { payout: DisbursementListItem }) {
  if (payout.status === "pending") {
    return (
      <p className="rounded-md border border-info/25 bg-info/10 p-3 text-sm">
        <strong className="font-medium">Payout reserved, waiting for the transfer.</strong>{" "}
        {formatIDR(payout.amount)} to {orDash(payout.bank_code)} {orDash(payout.destination)} ·{" "}
        {orDash(payout.bank_account_holder)}, reserved {formatDateTime(payout.created_at)} by{" "}
        {orDash(payout.created_by_email)}. Make the transfer from the bank, then mark it transferred with
        the bank's reference.
      </p>
    );
  }
  return (
    <p className="text-sm text-muted-foreground">
      Paid {formatIDR(payout.amount)} on {formatDateTime(payout.completed_at)} by{" "}
      {orDash(payout.completed_by_email)}, bank reference{" "}
      <span className="font-mono text-foreground">{orDash(payout.bank_reference)}</span>.
    </p>
  );
}

/**
 * Opens the printable document in a new tab. The route needs the staff token,
 * so the HTML is fetched and handed to the tab as a blob. The tab is opened
 * first, inside the click, because a window.open after an await is a popup
 * and gets blocked.
 */
function OpenDocumentButton({ merchantId, statement }: { merchantId: string; statement: InvoiceListItem }) {
  const fetchDocument = useFetchInvoiceDocument(merchantId);
  const [loading, setLoading] = useState(false);

  async function open() {
    const tab = window.open("", "_blank");
    if (!tab) {
      toast.error("The browser blocked the new tab. Allow pop-ups for this site and try again.");
      return;
    }
    tab.opener = null;
    tab.document.title = "Loading statement…";
    setLoading(true);
    try {
      const html = await fetchDocument(statement.invoice_id);
      const url = URL.createObjectURL(new Blob([html], { type: "text/html" }));
      tab.location.href = url;
      // Long enough for the tab to load it; the tab keeps its own copy after.
      setTimeout(() => URL.revokeObjectURL(url), 60_000);
    } catch (err) {
      tab.close();
      toast.error(`Couldn't open the statement: ${errorMessage(err)}`);
    } finally {
      setLoading(false);
    }
  }

  return (
    <Button variant="outline" size="sm" onClick={() => void open()} disabled={loading}>
      <FileTextIcon className="size-3.5" />
      {loading ? "Opening…" : statement.status === "draft" ? "Preview document" : "Open document"}
    </Button>
  );
}

function IssueAction({ merchantId, statement }: { merchantId: string; statement: InvoiceListItem }) {
  const [open, setOpen] = useState(false);
  const issue = useIssueInvoice(merchantId, statement.invoice_id);

  return (
    <>
      <Button size="sm" onClick={() => setOpen(true)}>
        Issue
      </Button>
      <ConfirmDialog
        open={open}
        onOpenChange={setOpen}
        title="Issue this statement?"
        description={describePeriod(statement)}
        confirmLabel="Issue statement"
        pending={issue.isPending}
        onConfirm={() =>
          issue.mutate(undefined, {
            onSuccess: (issued) => {
              setOpen(false);
              toast.success(`Issued ${issued.invoice_number ?? "the statement"}.`);
            },
          })
        }
        body={
          <>
            <ul className="list-disc space-y-1 pl-5">
              <li>It gets the next number in this year's series (INV-YYYY-…). Numbers have no gaps.</li>
              <li>
                PPN of {formatIDR(statement.totals.ppn)} is booked, so the merchant's balance drops by that and
                the statement's net payable is {formatIDR(statement.totals.net_payable)}.
              </li>
              <li>Nothing on it can be edited after this. A mistake is fixed by voiding it and issuing a new one.</li>
            </ul>
            {issue.error ? <p className="text-xs text-destructive">{errorMessage(issue.error)}</p> : null}
          </>
        }
      />
    </>
  );
}

function DiscardAction({ merchantId, statement }: { merchantId: string; statement: InvoiceListItem }) {
  const [open, setOpen] = useState(false);
  const discard = useVoidInvoice(merchantId, statement.invoice_id);

  return (
    <>
      <Button variant="ghost" size="sm" onClick={() => setOpen(true)}>
        Discard draft
      </Button>
      <ConfirmDialog
        open={open}
        onOpenChange={setOpen}
        title="Discard this draft?"
        description={describePeriod(statement)}
        confirmLabel="Discard draft"
        destructive
        pending={discard.isPending}
        onConfirm={() =>
          // A draft needs no reason: it was never numbered and booked nothing.
          discard.mutate("", {
            onSuccess: () => {
              setOpen(false);
              toast.success("Draft discarded. Its settlements can go on a new statement.");
            },
          })
        }
        body={
          <>
            <p>
              It was never numbered and booked nothing. Its settlements are released, so a new draft over the
              same days picks them up again.
            </p>
            {discard.error ? <p className="text-xs text-destructive">{errorMessage(discard.error)}</p> : null}
          </>
        }
      />
    </>
  );
}

/**
 * Withdraws an issued statement. Tier-2: it has a number the merchant may
 * already have filed, so the operator types that number and gives a reason.
 */
function VoidAction({ merchantId, statement }: { merchantId: string; statement: InvoiceListItem }) {
  const [open, setOpen] = useState(false);
  const [reason, setReason] = useState("");
  const voidStatement = useVoidInvoice(merchantId, statement.invoice_id);
  const number = statement.invoice_number ?? "";

  return (
    <>
      <Button variant="ghost" size="sm" onClick={() => setOpen(true)}>
        Void
      </Button>
      <TypedConfirmDialog
        open={open}
        onOpenChange={(next) => {
          setOpen(next);
          if (!next) setReason("");
        }}
        title={`Void ${number}?`}
        consequence={`Its PPN booking of ${formatIDR(statement.totals.ppn)} is reversed and its settlements are released for a new statement. It keeps its number, marked VOID.`}
        targetLabel="statement number"
        expectedValue={number}
        confirmLabel="Void statement"
        pending={voidStatement.isPending}
        confirmDisabled={!reason.trim()}
        onConfirm={() =>
          voidStatement.mutate(reason.trim(), {
            onSuccess: () => {
              setOpen(false);
              setReason("");
              toast.success(`${number} voided.`);
            },
          })
        }
        body={
          <>
            <div className="space-y-1.5">
              <Label htmlFor={`void-${statement.invoice_id}`} className="text-xs text-muted-foreground">
                Reason (kept on the statement and in the audit log)
              </Label>
              <Input
                id={`void-${statement.invoice_id}`}
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                placeholder="e.g. wrong period; replaced by the next statement"
              />
            </div>
            <p className="text-xs text-muted-foreground">
              If the merchant already received it, tell them it's withdrawn.
            </p>
            {voidStatement.error ? (
              <p className="text-xs text-destructive">{errorMessage(voidStatement.error)}</p>
            ) : null}
          </>
        }
      />
    </>
  );
}

/** The refusals a payout can get, each with its own next step. */
function payoutRefusal(err: unknown, merchantName: string): string {
  if (hasCode(err, ErrorCode.NoBankAccount)) {
    return `${merchantName} has no complete bank details on file. Add them to the merchant record first.`;
  }
  if (hasCode(err, ErrorCode.MerchantSuspended)) {
    return `${merchantName} is suspended. Payouts are refused until it's active again.`;
  }
  if (hasCode(err, ErrorCode.InsufficientBalance)) {
    return `The balance doesn't cover this statement (${errorMessage(err)}). Something was reversed after it was issued, or another payout reserved the money.`;
  }
  if (hasCode(err, ErrorCode.NothingToPayOut)) {
    return "This statement's net payable isn't above zero, so there's nothing to transfer.";
  }
  return errorMessage(err);
}

function PayAction({
  merchant,
  statement,
  balance,
}: {
  merchant: MerchantDetail;
  statement: InvoiceListItem;
  balance?: MerchantBalance;
}) {
  const [open, setOpen] = useState(false);
  const pay = usePayInvoice(merchant.merchant_id, statement.invoice_id);

  return (
    <>
      <Button size="sm" onClick={() => setOpen(true)}>
        Pay
      </Button>
      <ConfirmDialog
        open={open}
        onOpenChange={setOpen}
        title={`Pay ${statement.invoice_number}?`}
        description={`${formatIDR(statement.totals.net_payable)} to ${merchant.name}`}
        confirmLabel="Reserve payout"
        pending={pay.isPending}
        onConfirm={() =>
          pay.mutate(undefined, {
            onSuccess: () => {
              setOpen(false);
              toast.success("Payout reserved. Make the transfer, then mark it transferred.");
            },
          })
        }
        body={
          <>
            <FieldGrid className="sm:grid-cols-2 lg:grid-cols-2">
              <DetailField label="Amount">{formatIDR(statement.totals.net_payable)}</DetailField>
              <DetailField label="To">
                {merchant.has_bank_account ? bankLine(merchant) : "no bank details on file"}
              </DetailField>
              <DetailField label="Balance now">{balance ? formatIDR(balance.payable) : EM_DASH}</DetailField>
            </FieldGrid>
            <p>
              This doesn't send money. It takes the amount off the merchant's balance at once, so it can't be
              paid twice, and records that a transfer is due. You make the transfer from the bank, then mark it
              transferred here with the bank's reference. If the transfer doesn't happen, mark it failed and the
              money goes back.
            </p>
            {pay.error ? <p className="text-xs text-destructive">{payoutRefusal(pay.error, merchant.name)}</p> : null}
          </>
        }
      />
    </>
  );
}

function CompleteAction({
  merchantId,
  statement,
  payout,
}: {
  merchantId: string;
  statement: InvoiceListItem;
  payout: DisbursementListItem;
}) {
  const [open, setOpen] = useState(false);
  const [reference, setReference] = useState("");
  const complete = useCompleteDisbursement(merchantId, statement.invoice_id);

  return (
    <>
      <Button size="sm" onClick={() => setOpen(true)}>
        Mark transferred
      </Button>
      <ConfirmDialog
        open={open}
        onOpenChange={(next) => {
          setOpen(next);
          if (!next) setReference("");
        }}
        title="Mark the transfer as made?"
        description={`${formatIDR(payout.amount)} for ${statement.invoice_number}`}
        confirmLabel="Mark transferred"
        pending={complete.isPending}
        confirmDisabled={!reference.trim()}
        onConfirm={() => {
          complete.mutate(
            { disbursementId: payout.disbursement_id, bankReference: reference.trim() },
            {
              onSuccess: () => {
                setOpen(false);
                setReference("");
                toast.success(`${statement.invoice_number} is paid.`);
              },
            },
          );
        }}
        body={
          <>
            <div className="space-y-1.5">
              <Label htmlFor={`ref-${payout.disbursement_id}`} className="text-xs text-muted-foreground">
                Bank reference of the transfer
              </Label>
              <Input
                id={`ref-${payout.disbursement_id}`}
                value={reference}
                maxLength={128}
                onChange={(e) => setReference(e.target.value)}
                placeholder="from the bank's confirmation"
              />
            </div>
            <p>
              Only once the money has actually left. The statement becomes paid, and this can't be undone from
              here.
            </p>
            {complete.error ? <p className="text-xs text-destructive">{errorMessage(complete.error)}</p> : null}
          </>
        }
      />
    </>
  );
}

function FailAction({
  merchantId,
  statement,
  payout,
}: {
  merchantId: string;
  statement: InvoiceListItem;
  payout: DisbursementListItem;
}) {
  const [open, setOpen] = useState(false);
  const [reason, setReason] = useState("");
  const fail = useFailDisbursement(merchantId, statement.invoice_id);

  return (
    <>
      <Button variant="ghost" size="sm" onClick={() => setOpen(true)}>
        Mark failed
      </Button>
      <ConfirmDialog
        open={open}
        onOpenChange={(next) => {
          setOpen(next);
          if (!next) setReason("");
        }}
        title="Mark this payout as failed?"
        description={`${formatIDR(payout.amount)} for ${statement.invoice_number}`}
        confirmLabel="Mark failed"
        destructive
        pending={fail.isPending}
        confirmDisabled={!reason.trim()}
        onConfirm={() => {
          fail.mutate(
            { disbursementId: payout.disbursement_id, reason: reason.trim() },
            {
              onSuccess: () => {
                setOpen(false);
                setReason("");
                toast.success("Payout failed. The money is back on the balance, and the statement can be paid again.");
              },
            },
          );
        }}
        body={
          <>
            <div className="space-y-1.5">
              <Label htmlFor={`fail-${payout.disbursement_id}`} className="text-xs text-muted-foreground">
                Reason (kept on the payout and in the audit log)
              </Label>
              <Input
                id={`fail-${payout.disbursement_id}`}
                value={reason}
                maxLength={1000}
                onChange={(e) => setReason(e.target.value)}
                placeholder="e.g. bank rejected: account closed"
              />
            </div>
            <p>
              Only if no money left. The reserve is reversed, so the amount goes back on the balance, and the
              statement can be paid again.
            </p>
            {fail.error ? <p className="text-xs text-destructive">{errorMessage(fail.error)}</p> : null}
          </>
        }
      />
    </>
  );
}
